import { type IRelaySession, newClientUploadId } from './media-relay';
import type { CaptureMode, IVideoChapter } from './result-video-api';

// On-device record of one result video, keyed by (election, polling unit) — there is one
// current video per unit, so recording again REPLACES the local record. Written before any
// byte leaves the phone; the flusher (result-video-sync.ts) uploads it on `online`, on app
// start and on a slow tick. Records are never silently deleted: after the retry ladder runs
// out a record is `held` and waits for a manual Retry.
//
// v2 (MediaStash relay): the upload session is api.core's, and the resume point is the server's
// `receivedChunks`, not a stored offset. A v1 record is migrated once on read — see
// `migrateVideoRecord`. The storage key keeps its `v1` prefix so no record is ever orphaned.

export type VideoDeliveryState =
	| 'queued'
	/** Chunks are going up; the server's session map is the resume point. */
	| 'uploading'
	/** Storage has the file; registering it with api.core is what's left. */
	| 'uploaded'
	| 'registered'
	/** Retries exhausted — waiting for the agent to tap Retry. */
	| 'held'
	/** The server refused it for good (window closed, not assigned, turned off). Never retried. */
	| 'refused'
	/** The bytes are gone (no IndexedDB and the page reloaded). Record again. */
	| 'missing';

export interface IResultVideoRecord {
	v: 2;
	/** Local id — the IndexedDB key for the blob. */
	id: string;
	/** UUID sent on every `sign` for this recording — the server's idempotency key for the asset. */
	clientUploadId: string;
	electionId: number;
	/** Core user id of the recorder — another agent on a shared phone never sends it. */
	userId?: number;
	pollingUnitCode: string;
	pollingUnitName?: string;
	resultReferenceId: string;
	mime: string;
	bytes: number;
	fileName: string;
	durationSec?: number;
	sha256?: string;
	captureMode: CaptureMode;
	captureStartedAt?: string;
	captureEndedAt?: string;
	coords?: { latitude: number; longitude: number; accuracy?: number | null } | null;
	chapters: IVideoChapter[];
	deviceInfo?: string;
	/** When the window to add this video closes, as the server last said. */
	windowClosesAt?: string;
	createdAt: number;

	state: VideoDeliveryState;
	attempts: number;
	nextAttemptAt: number;
	error?: string;
	code?: string;

	/** api.core upload session — kept so a dropped connection resumes instead of starting over. */
	session?: IRelaySession;
	/** Bytes the server has acknowledged, for the progress bar only — never the resume point. */
	uploadedBytes: number;
	/** Set once storage holds the whole file (complete, or `alreadyStored` on sign). */
	storagePublicId?: string;
	registeredAt?: string;
	/** Hold uploads until the phone is on Wi-Fi (only offered where the browser can tell). */
	wifiOnly?: boolean;
}

const PREFIX = 'result-video:v1:';
const recordKey = (electionId: number, puCode: string) => `${PREFIX}${electionId}:${puCode}`;
const CHANGED_EVENT = 'result-video:changed';

const notifyChanged = (electionId: number, puCode: string) => {
	try {
		window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { electionId, puCode } }));
	} catch {
		// Non-browser environment — nothing to notify.
	}
};

/** Re-render every surface showing this unit — also used when a sync starts or ends. */
export const notifyVideoChanged = (electionId: number, puCode: string) => notifyChanged(electionId, puCode);

/** Fires on any change to this unit's record, in this tab or another. */
export const subscribeResultVideo = (electionId: number, puCode: string, onChange: () => void): (() => void) => {
	const handler = (event: Event) => {
		const detail = (event as CustomEvent<{ electionId: number; puCode: string }>).detail;
		if (detail?.electionId === electionId && detail?.puCode === puCode) onChange();
	};
	const onStorage = (event: StorageEvent) => {
		if (event.key === recordKey(electionId, puCode)) onChange();
	};
	window.addEventListener(CHANGED_EVENT, handler);
	window.addEventListener('storage', onStorage);
	return () => {
		window.removeEventListener(CHANGED_EVENT, handler);
		window.removeEventListener('storage', onStorage);
	};
};

// In-session copy for when storage throws, so the flow still works online.
const memory = new Map<string, IResultVideoRecord>();

const isRecord = (v: unknown): v is IResultVideoRecord =>
	!!v && typeof v === 'object' && (v as IResultVideoRecord).v === 2 && typeof (v as IResultVideoRecord).id === 'string';

const isLegacyRecord = (v: unknown): v is Record<string, unknown> & { v: 1; id: string; state: VideoDeliveryState } =>
	!!v && typeof v === 'object' && (v as { v?: unknown }).v === 1 && typeof (v as { id?: unknown }).id === 'string';

/** States still on their way — a v1 record in one of these restarts on the relay. */
const UNFINISHED: VideoDeliveryState[] = ['queued', 'uploading', 'uploaded', 'held'];

/**
 * v1 → v2, once. The v1 upload state belonged to the retired Cloudinary cloud: its signature,
 * upload id and byte offset are dropped, and so is a Cloudinary `storagePublicId`/`url` on a
 * record that was never registered (the server now verifies the asset in MediaStash, so that
 * file must go up again). Anything unfinished — including `held`, which is where most videos
 * stalled by the outage sit — becomes `queued` with a fresh retry ladder, so it uploads without
 * a tap. Terminal states are kept as they are. The blob is never touched.
 */
export const migrateVideoRecord = (legacy: Record<string, unknown>): IResultVideoRecord => {
	const { v: _v, signed: _signed, signedAt: _signedAt, uploadId: _uploadId, offset: _offset, url: _url, ...rest } = legacy;
	const base = rest as unknown as Omit<IResultVideoRecord, 'v' | 'clientUploadId' | 'uploadedBytes'>;
	const unfinished = UNFINISHED.includes(base.state);
	return {
		...base,
		v: 2,
		clientUploadId: newClientUploadId(),
		uploadedBytes: 0,
		...(unfinished
			? {
					state: 'queued' as const,
					attempts: 0,
					nextAttemptAt: 0,
					error: undefined,
					code: undefined,
					storagePublicId: undefined,
				}
			: {}),
	};
};

/** Parses a stored value, migrating (and persisting) a v1 record. */
const readStored = (key: string, raw: string | null): IResultVideoRecord | null => {
	const parsed = raw ? JSON.parse(raw) : null;
	if (isRecord(parsed)) return parsed;
	if (!isLegacyRecord(parsed)) return null;
	const migrated = migrateVideoRecord(parsed);
	try {
		localStorage.setItem(key, JSON.stringify(migrated));
	} catch {
		memory.set(key, migrated);
	}
	return migrated;
};

export const getVideoRecord = (electionId: number, puCode: string): IResultVideoRecord | null => {
	const key = recordKey(electionId, puCode);
	try {
		const stored = readStored(key, localStorage.getItem(key));
		if (stored) return stored;
	} catch {
		// Fall through to memory.
	}
	return memory.get(key) ?? null;
};

export const writeVideoRecord = (record: IResultVideoRecord): IResultVideoRecord => {
	const key = recordKey(record.electionId, record.pollingUnitCode);
	try {
		localStorage.setItem(key, JSON.stringify(record));
		memory.delete(key);
	} catch {
		memory.set(key, record);
	}
	notifyChanged(record.electionId, record.pollingUnitCode);
	return record;
};

export const updateVideoRecord = (
	electionId: number,
	puCode: string,
	patch: Partial<IResultVideoRecord> | ((r: IResultVideoRecord) => IResultVideoRecord)
): IResultVideoRecord | null => {
	const current = getVideoRecord(electionId, puCode);
	if (!current) return null;
	return writeVideoRecord(typeof patch === 'function' ? patch(current) : { ...current, ...patch });
};

export const removeVideoRecord = (electionId: number, puCode: string) => {
	const key = recordKey(electionId, puCode);
	try {
		localStorage.removeItem(key);
	} catch {
		// Storage unavailable.
	}
	memory.delete(key);
	notifyChanged(electionId, puCode);
};

/** Every record on this device — the app-start flush walks these. */
export const listVideoRecords = (): IResultVideoRecord[] => {
	const out = new Map<string, IResultVideoRecord>(memory);
	try {
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (!key?.startsWith(PREFIX)) continue;
			const stored = readStored(key, localStorage.getItem(key));
			if (stored) out.set(key, stored);
		}
	} catch {
		// Storage unavailable — memory only.
	}
	return [...out.values()];
};

export const newVideoRecord = (
	input: Omit<IResultVideoRecord, 'v' | 'clientUploadId' | 'state' | 'attempts' | 'nextAttemptAt' | 'uploadedBytes'> & {
		clientUploadId?: string;
	}
): IResultVideoRecord => ({
	v: 2,
	...input,
	clientUploadId: input.clientUploadId ?? newClientUploadId(),
	state: 'queued',
	attempts: 0,
	nextAttemptAt: 0,
	uploadedBytes: 0,
});

/** Upload progress 0–100 from what the server has acknowledged. */
export const uploadedPercent = (r: IResultVideoRecord): number =>
	r.bytes ? Math.min(100, Math.round((r.uploadedBytes / r.bytes) * 100)) : 0;

/** Still on its way — the card shows progress, and a re-record must confirm it replaces this. */
export const isPending = (r: IResultVideoRecord | null): boolean =>
	!!r && (r.state === 'queued' || r.state === 'uploading' || r.state === 'uploaded' || r.state === 'held');
